import { createI18n, useI18n as useI18nOriginal } from "vue-i18n";
import { isString } from "tcheck";
import { en, es } from "vuetify/locale";
import translations from "./standardTranslations";

const dateTimeFormats = {
  en: {
    shortDate: {
      year: "numeric",
      month: "short",
      day: "numeric",
    },
    longDate: {
      year: "numeric",
      month: "long",
      day: "numeric",
      weekday: "long",
    },
    dateTime: {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    },
    timeShort: {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    },
    timeLong: {
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: true,
    },
    numericDate: {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    },
  },
  es: {
    shortDate: {
      year: "numeric",
      month: "short",
      day: "numeric",
    },
    longDate: {
      year: "numeric",
      month: "long",
      day: "numeric",
      weekday: "long",
    },
    dateTime: {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    },
    timeShort: {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    },
    timeLong: {
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: true,
    },
    numericDate: {
      year: "numeric",
      day: "2-digit",
      month: "2-digit",
    },
  },
};

const i18n = createI18n({
  legacy: false,
  locale: "en",
  fallbackLocale: "en",
  messages: {
    es: {
      $vuetify: es,
    },
    en: {
      $vuetify: en,
    },
  },
  datetimeFormats: dateTimeFormats,
  missingWarn: false,
});

// Handles various date string formats
export function convertStringToDate(dateStr, format = "numericDate") {
  if (typeof dateStr != "undefined") {
    if (dateStr.includes("/")) {
      // EX: MM/DD/YYYY
      const [month, day, year] = dateStr.split("/").map(Number);
      return i18n.global.d(new Date(year, month - 1, day), format);
    } else if (dateStr.includes("-")) {
      // EX: YYYY-MM-DD
      const [year, month, day] = dateStr.split("-").map(Number);
      const date = new Date(year, month - 1, day);
      return i18n.global.d(date, format);
    }
  }
}

// Handles YYYY-MM-DD format (e.g., '2025-04-20')
export function formatDateFromDashedISO(dateStr, format = "numericDate") {
  if (typeof dateStr !== "undefined") {
    const [year, month, day] = dateStr.split("-").map(Number);
    const localDate = new Date(year, month - 1, day);
    return i18n.global.d(localDate, format);
  }
}

export async function setLocale(localeParam = "en") {
  try {
    i18n.global.locale.value = localeParam.toLowerCase();
  } catch (error) {
    console.error("Error loading locale messages:", error);
    i18n.global.locale = "en";
  }
}

export function getLocale() {
  return i18n.global.locale.value;
}

export function getBrowserLocale() {
  const allowedLocales = ["en", "es"];

  const browserLocale = navigator.language || navigator.userLanguage;
  if (!browserLocale) {
    return "en";
  }

  const localeCode = browserLocale.split("-")[0].toLowerCase();

  if (allowedLocales.includes(localeCode)) {
    return localeCode;
  }

  return "en";
}

/**
 * Returns a new instance of the i18n object.
 * @param {object} options - The options for the i18n object.
 * @returns {i18n}
 */
export function useI18n(options) {
  options.messages.en = { ...options.messages.en, ...translations.en };
  options.messages.es = { ...options.messages.es, ...translations.es };

  return useI18nOriginal({
    ...options,
    locale: options.locale || "en",
    fallbackLocale: options.fallbackLocale || "en",
    messages: options.messages,
    datetimeFormats: options.datetimeFormats || dateTimeFormats,
  });
}

export default i18n;
